<template>
  <div class="report-templates">
    <Breadcrumbs
      :breadcrumbs="[
        { title: this.$t('menu.reports'), disabled: true },
        { title: this.$t('menu.reports/templates'), disabled: true }
      ]">
      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <Searchable class="mr-2" :querySearch.sync="querySearch"/>

        <div class="btn btn__floating btn__round__large btn__round__large">
          <va-popover
            class="mr-3"
            placement="left"
            :message="$t('reports.templates.titles.create_modal')"
          >
            <span class="btn__round">
              <va-button
                id="btn-create-report"
                @click.prevent="createTemplate">
                <i class="fa fa-plus"></i>
              </va-button>
            </span>
          </va-popover>
        </div>
      </div>
    </Breadcrumbs>
    <div>
      <ReportTemplatesTable
        ref="reportTemplatesTableIndex"
        :querySearch="querySearch"
        :serviceUrl="component.service.url"
        :clusters="clusters"
        :facilities="facilities"
        :reportTypesAdapter="reportTypesAdapter"
        @update-template="updateTemplate"
        @delete-template="deleteTemplate"
        @generate-report="generateReport"
      />
      <CreateUpdateShowTemplate
        v-if="selectedReportTemplateForUpdate !== undefined"
        :serviceUrl="component.service.url"
        :reportTemplate="selectedReportTemplateForUpdate"
        :clusters="clusters"
        :facilities="facilities"
        :entities="crossedWritableTemplateEntities"
        @modalWasClosed="onCreateUpdateShowReportModalWasClosed"
        @onCreateOrUpdate="onCreateOrUpdate"
      />

      <GenerateReport
        v-if="selectedReportTemplateForGeneration !== undefined"
        :serviceUrl="component.service.url"
        :reportTemplate="selectedReportTemplateForGeneration"
        :reportTypesAdapter="reportTypesAdapter"
        :writableEntities="writableEntities"
        @modalWasClosed="onGenerateReportModalClosed"
        @report-generated="reportGenerated"
      />

      <ConfirmationModal
        v-if="selectedReportTemplateForDeletion !== undefined"
        :title="$t('reports.templates.titles.delete_modal')"
        :body="$t('reports.templates.messages.delete_modal_body', {name: selectedReportTemplateForDeletion.name})"
        okClass="btn-danger"
        @modal-confirmed="confirmDelete"
        @modal-canceled="cancelDelete"
      />

    </div>
  </div>
</template>
<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import Searchable from '@/components/common/Searchable'
import ReportTemplatesTable from '@/components/reports/templates/ReportTemplatesTable'
import { TABLE_REPORT_TEMPLATES } from '@/local-storage/LocalStorageKeys'
import IndexTablesMixin from '@/mixins/IndexTablesMixin'
import CreateUpdateShowTemplate from '@/components/reports/templates/CreateUpdateShowTemplate'
import { ReportTemplatesService } from '@/api/ReportsService'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import GenerateReport from '@/components/reports/templates/GenerateReport'

const getFacilitiesRoute = '/facilitiesaccess'
const getClustersRoute = '/clusters?withFacilities=true'

export default {
  components: { GenerateReport, ConfirmationModal, CreateUpdateShowTemplate, ReportTemplatesTable, Searchable, Breadcrumbs },
  mixins: [
    IndexTablesMixin
  ],
  props: {
    component: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      clusters: [],
      facilities: [],
      crossedWritableTemplateEntities: [],
      writableEntities: [],
      querySearch: '',
      localStorageKey: TABLE_REPORT_TEMPLATES,
      selectedReportTemplateForUpdate: undefined,
      selectedReportTemplateForDeletion: undefined,
      selectedReportTemplateForGeneration: undefined,
      reportTypesAdapter: {
        1: 'monthlyView',
        2: 'actionView'
      },
    }
  },
  mounted: function () {
    Promise.all([this.loadClusters(), this.loadFacilities()])
      .then(values => {
        this.clusters = values[0]
        this.facilities = values[1]
      })
  },
  methods: {
    refreshTable: function () {
      this.$refs.reportTemplatesTableIndex.$refs.reportTemplatesTable.refresh()
    },
    loadFacilities: function () {
      return ReportTemplatesService.loadFacilities(getFacilitiesRoute)
        .then(response => response)
        .catch(errors => this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions))
    },
    loadClusters: function () {
      return ReportTemplatesService.loadClusters(getClustersRoute)
        .then(response => response)
        .catch(errors => this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions))
    },
    createTemplate: function () {
      this.selectedReportTemplateForUpdate = {}
    },
    updateTemplate: function (data) {
      this.crossedWritableTemplateEntities = data.crossedWritableTemplateEntities
      this.selectedReportTemplateForUpdate = data.template
    },
    onCreateUpdateShowReportModalWasClosed: function () {
      this.selectedReportTemplateForUpdate = undefined
    },
    onCreateOrUpdate: function (payload) {
      if (payload.method === 'POST') {
        this.showToast(this.$t('reports.templates.messages.successful_store'), this.$store.state.successToastOptions)
      } else {
        this.selectedReportTemplateForUpdate = undefined
        this.showToast(this.$t('reports.templates.messages.successful_update'), this.$store.state.successToastOptions)
      }
      this.refreshTable()
    },
    deleteTemplate: function (report) {
      this.selectedReportTemplateForDeletion = report
    },
    confirmDelete: function (report) {
      ReportTemplatesService.destroy(this.selectedReportTemplateForDeletion.id)
        .then(response => {
          this.refreshTable()
          this.showToast(this.$t('reports.templates.messages.successful_delete'), this.$store.state.successToastOptions)
        })
        .catch(errors => {
          if (errors.response.status === 403 && errors.response.data.hasOwnProperty('message')) {
            this.showToast(this.$t(`reports.templates.messages.${errors.response.data.message}`), this.$store.state.errorToastOptions)
          } else if (errors.response.status === 422) {
            this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
        .finally(() => {
          this.selectedReportTemplateForDeletion = undefined
        })
    },
    onGenerateReportModalClosed: function () {
      this.selectedReportTemplateForGeneration = undefined
    },
    cancelDelete: function () {
      this.selectedReportTemplateForDeletion = undefined
    },
    generateReport: function (data) {
      this.writableEntities = data.writableEntities
      this.selectedReportTemplateForGeneration = data.report
    },
    reportGenerated: function () {
      this.selectedReportTemplateForGeneration = undefined
      this.showToast(this.$t('reports.templates.messages.successful_generate'), this.$store.state.successToastOptions)
    }
  }
}

</script>

<style lang="scss">

</style>
